import type { AddressFallbackRecord } from '@navio/nsdk';

/**
 * Manual billing-only address create requires lat/lng on AddressRecordCreate.
 * Coords are not used for stationary/POS routing — only street + postal identity.
 */
const BILLING_ONLY_COORDINATES = { latitude: 0, longitude: 0 } as const;

/** Street line from manual entry is `"Name 12"` — split last token as number when it looks like one. */
export function parseStreetFromFallbackAddress(addressLine: string): {
  streetName: string;
  streetNumber: string;
} {
  const trimmed = addressLine.trim();
  const match = trimmed.match(/^(.*?)(?:\s+(\d+\s*[A-Za-z]?))?$/);
  const streetName = match?.[1]?.trim() || trimmed;
  const streetNumber = match?.[2]?.trim() ?? '';
  return { streetName, streetNumber };
}

export function isAddressFallbackComplete(fallback: AddressFallbackRecord | null | undefined): boolean {
  return (
    !!fallback &&
    fallback.address.trim().length > 0 &&
    fallback.zipcode.trim().length > 0 &&
    fallback.city.trim().length > 0
  );
}

/** Nested `address` body for postUserGroupAddress (manual entry path). */
export function buildManualProfileAddressBody(
  fallback: AddressFallbackRecord,
  countryCode: string
): {
  streetName: string;
  streetNumber: string;
  zipCode: string;
  city: string;
  countryCode: string;
  latitude: number;
  longitude: number;
} {
  const { streetName, streetNumber } = parseStreetFromFallbackAddress(fallback.address);
  return {
    streetName,
    // BE requires a street number; use a sentinel when the user only entered a street name.
    streetNumber: streetNumber || '0',
    zipCode: fallback.zipcode.trim(),
    city: fallback.city.trim(),
    countryCode,
    ...BILLING_ONLY_COORDINATES
  };
}
